// src/services/organisationService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc,
  getDocs, 
  query, 
  serverTimestamp,
  where, 
  orderBy,
  limit 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

export const organisationService = {
  // ==================== CONTENT SECTIONS ====================

  // Get all sections for an organisation
  async getOrganisationSections(bookId) {
    try {
      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      const q = query(sectionsRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const sections = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return sections;
    } catch (error) {
      console.error('Error getting organisation sections:', error);
      throw error;
    }
  },

  // Get a specific section
  async getOrganisationSection(bookId, sectionId) {
    try {
      const sectionRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      const docSnap = await getDoc(sectionRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting organisation section:', error);
      throw error;
    }
  },

  // Add a new section
  async addOrganisationSection(bookId, sectionData) {
    try {
      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      const docRef = await addDoc(sectionsRef, {
        ...sectionData,
        bookId,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      return { id: docRef.id, ...sectionData };
    } catch (error) {
      console.error('Error adding organisation section:', error);
      throw error;
    }
  },

  // Update a section
  async updateOrganisationSection(bookId, sectionId, sectionData) {
    try {
      const sectionRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      await updateDoc(sectionRef, {
        ...sectionData,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating organisation section:', error);
      throw error;
    }
  },

  // Delete a section
  async deleteOrganisationSection(bookId, sectionId) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      await deleteDoc(docRef);
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting organisation section:', error);
      throw error;
    }
  },

  // Initialize default sections for new organisation
  async initializeOrganisationSections(bookId) {
    try {
      const defaultSections = [
        {
          id: 'hero',
          type: 'hero',
          enabled: true,
          heading: '',
          subHeading: '',
          description: '',
          backgroundImage: '',
          primaryButtonText: '',
          primaryButtonUrl: '',
          layout: 'center',
          order: 1
        },
        {
          id: 'about',
          type: 'about',
          enabled: true,
          heading: 'About Us',
          subHeading: '',
          description: '',
          mission: '',
          vision: '',
          values: [],
          statistics: [],
          image: '',
          layout: 'split',
          order: 2
        },
        {
          id: 'leadership',
          type: 'leadership',
          enabled: false,
          heading: 'Our Leadership Team',
          subHeading: '',
          leaders: [],
          layout: 'grid',
          order: 3
        },
        {
          id: 'services',
          type: 'services',
          enabled: false,
          heading: 'Our Services',
          subHeading: '',
          services: [],
          layout: 'grid',
          order: 4
        },
        {
          id: 'events',
          type: 'events',
          enabled: false,
          heading: 'Upcoming Events',
          subHeading: '',
          events: [],
          layout: 'grid',
          order: 5
        },
        {
          id: 'news',
          type: 'news',
          enabled: false,
          heading: 'Latest News',
          subHeading: '',
          articles: [],
          layout: 'grid',
          order: 6
        },
        {
          id: 'contact',
          type: 'contact',
          enabled: true,
          heading: 'Get In Touch',
          subHeading: '',
          phone: '',
          email: '',
          address: '',
          businessHours: [],
          showForm: true,
          mapUrl: '',
          order: 7
        }
      ];

      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      
      // Create each section
      for (const section of defaultSections) {
        await addDoc(sectionsRef, {
          ...section,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      return defaultSections;
    } catch (error) {
      console.error('Error initializing organisation sections:', error);
      throw error;
    }
  },

  // ==================== MANAGEMENT OPERATIONS ====================

  // Get management configuration
  async getManagementConfig(bookId) {
    try {
      const managementRef = collection(db, 'pb-books', bookId, 'pb-management');
      const q = query(managementRef, where('type', '==', 'config'));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting management config:', error);
      return null;
    }
  },

  // Update management configuration
  async updateManagementConfig(bookId, configData) {
    try {
      const managementRef = collection(db, 'pb-books', bookId, 'pb-management');
      
      // Check if config exists
      const q = query(managementRef, where('type', '==', 'config'));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Update existing config
        const configDoc = querySnapshot.docs[0];
        await updateDoc(configDoc.ref, {
          ...configData,
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new config
        await addDoc(managementRef, {
          ...configData,
          type: 'config',
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error updating management config:', error);
      throw error;
    }
  },

  // Get management feature data
  async getManagementFeatureData(bookId, featureType) {
    try {
      const managementRef = collection(db, 'pb-books', bookId, 'pb-management');
      const q = query(managementRef, where('featureType', '==', featureType));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error(`Error getting ${featureType} management data:`, error);
      return null;
    }
  },

  // Update management feature data
  async updateManagementFeatureData(bookId, featureType, featureData) {
    try {
      const managementRef = collection(db, 'pb-books', bookId, 'pb-management');
      
      // Check if feature data exists
      const q = query(managementRef, where('featureType', '==', featureType));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Update existing feature data
        const featureDoc = querySnapshot.docs[0];
        await updateDoc(featureDoc.ref, {
          ...featureData,
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new feature data
        await addDoc(managementRef, {
          ...featureData,
          featureType,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error(`Error updating ${featureType} management data:`, error);
      throw error;
    }
  },

  // Log activity for records management
  async logActivity(bookId, activityData) {
    try {
      const activitiesRef = collection(db, 'pb-books', bookId, 'pb-activities');
      
      const activity = {
        ...activityData,
        bookId,
        createdAt: new Date(),
        id: Date.now().toString()
      };

      await addDoc(activitiesRef, activity);
      
      return activity;
    } catch (error) {
      console.error('Error logging activity:', error);
      throw error;
    }
  },

  // Get recent activities
  async getRecentActivities(bookId, limitCount = 50) {
    try {
      const activitiesRef = collection(db, 'pb-books', bookId, 'pb-activities');
      const q = query(activitiesRef, orderBy('createdAt', 'desc'), limit(limitCount));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting recent activities:', error);
      return [];
    }
  },

  // ==================== FILE OPERATIONS ====================

  // Upload organisation media
  async uploadOrganisationMedia(file, bookId, sectionType, itemIndex = null) {
    try {
      // Validate file type and size
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }

      const allowedTypes = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf', 'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error(`File type ${file.type} not supported. Allowed types: images, PDF, Word documents`);
      }

      // Create unique filename with proper path structure
      const timestamp = Date.now();
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const itemPath = itemIndex !== null ? `item-${itemIndex}` : 'main';
      const fileName = `pb-${bookId}/${sectionType}/${timestamp}_${cleanFileName}`;

      console.log(`Uploading file to: ${fileName}`);

      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log(`Upload successful: ${downloadURL}`);

      return {
        url: downloadURL,
        fileName: cleanFileName,
        fileType: file.type,
        uploadedAt: new Date().toISOString(),
        path: fileName
      };
    } catch (error) {
      console.error('Error uploading organisation media:', error);
      
      // Provide user-friendly error messages
      if (error.code === 'storage/unauthorized') {
        throw new Error('Upload permission denied. Please check your access rights.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload was canceled');
      } else if (error.code === 'storage/quota-exceeded') {
        throw new Error('Storage quota exceeded');
      } else if (error.code === 'storage/invalid-argument') {
        throw new Error('Invalid file or upload parameters');
      } else if (error.message.includes('Firebase ID token has expired')) {
        throw new Error('Session expired. Please refresh the page and try again.');
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  },

  // Delete organisation media
  async deleteOrganisationMedia(mediaUrl) {
    try {
      if (mediaUrl && mediaUrl.includes('firebase')) {
        const mediaRef = ref(storage, mediaUrl);
        await deleteObject(mediaRef);
      }
      return { success: true };
    } catch (error) {
      console.error('Error deleting organisation media:', error);
      return { success: false, error: error.message };
    }
  },

  // Upload multiple files
  async uploadMultipleFiles(files, bookId, sectionType, onProgress) {
    const uploadPromises = files.map(async (file, index) => {
      try {
        const result = await this.uploadOrganisationMedia(file, bookId, sectionType, index);
        
        if (onProgress) {
          onProgress(index + 1, files.length);
        }
        
        return { success: true, index, ...result };
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        return { success: false, index, error: error.message, fileName: file.name };
      }
    });

    return await Promise.all(uploadPromises);
  },

  // ==================== ENGAGEMENT TRACKING ====================

  // Track engagement (views, likes, etc.)
  async trackEngagement(bookId, engagementType, data) {
    try {
      const engagementRef = collection(db, 'pb-books', bookId, 'pb-engagements');
      
      // Check if user already has this engagement
      if (data.userId) {
        const existingQ = query(
          engagementRef,
          where('userId', '==', data.userId),
          where('type', '==', engagementType)
        );
        const existingDocs = await getDocs(existingQ);
        
        if (existingDocs.docs.length > 0 && !['view', 'share'].includes(engagementType)) {
          // Toggle engagement for like, bookmark, etc.
          const existingDoc = existingDocs.docs[0];
          await deleteDoc(existingDoc.ref);
          
          // Get new total
          const totalCount = await this.getEngagementCount(bookId, engagementType);
          return { userStatus: false, newTotal: totalCount };
        }
      }

      // Add new engagement
      await addDoc(engagementRef, {
        type: engagementType,
        bookId,
        userId: data.userId || 'anonymous',
        userEmail: data.userEmail || null,
        data: data,
        createdAt: serverTimestamp(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
      });

      // Get new total
      const totalCount = await this.getEngagementCount(bookId, engagementType);
      
      return { userStatus: true, newTotal: totalCount };
    } catch (error) {
      console.error('Error tracking engagement:', error);
      throw error;
    }
  },

  // Get engagement data
  async getEngagementData(bookId, userId = null) {
    try {
      const engagementRef = collection(db, 'pb-books', bookId, 'pb-engagements');
      const allEngagements = await getDocs(engagementRef);
      
      const totals = {
        likes: 0,
        shares: 0,
        bookmarks: 0,
        interests: 0,
        views: 0
      };
      
      const userEngagement = {
        liked: false,
        shared: false,
        bookmarked: false,
        expressed_interest: false
      };

      allEngagements.docs.forEach(doc => {
        const data = doc.data();
        const type = data.type;
        
        // Count totals
        if (type === 'like') totals.likes++;
        else if (type === 'shared') totals.shares++;
        else if (type === 'bookmarked') totals.bookmarks++;
        else if (type === 'expressed_interest') totals.interests++;
        else if (type === 'view') totals.views++;

        // Check user engagement
        if (userId && data.userId === userId) {
          if (type === 'like') userEngagement.liked = true;
          else if (type === 'shared') userEngagement.shared = true;
          else if (type === 'bookmarked') userEngagement.bookmarked = true;
          else if (type === 'expressed_interest') userEngagement.expressed_interest = true;
        }
      });

      return { totals, userEngagement };
    } catch (error) {
      console.error('Error getting engagement data:', error);
      return { 
        totals: { likes: 0, shares: 0, bookmarks: 0, interests: 0, views: 0 },
        userEngagement: { liked: false, shared: false, bookmarked: false, expressed_interest: false }
      };
    }
  },

  // Get engagement count
  async getEngagementCount(bookId, engagementType) {
    try {
      const engagementRef = collection(db, 'pb-books', bookId, 'pb-engagements');
      const q = query(engagementRef, where('type', '==', engagementType));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.length;
    } catch (error) {
      console.error('Error getting engagement count:', error);
      return 0;
    }
  },

  // Update organisation settings (updates the main book document)
  async updateOrganisationSettings(bookId, settings) {
    try {
      const bookRef = doc(db, 'pb-books', bookId);
      await updateDoc(bookRef, {
        organisationSettings: settings,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating organisation settings:', error);
      throw error;
    }
  },

  // Get organisation section statistics
  async getOrganisationSectionStats(bookId) {
    try {
      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      const querySnapshot = await getDocs(sectionsRef);
      
      const stats = {
        total: 0,
        byType: {},
        enabled: 0,
        disabled: 0,
        totalViews: 0,
        totalInteractions: 0
      };

      querySnapshot.docs.forEach(doc => {
        const section = doc.data();
        stats.total++;

        // Count by type
        const type = section.type || 'unknown';
        stats.byType[type] = (stats.byType[type] || 0) + 1;

        // Count enabled/disabled
        if (section.enabled) stats.enabled++;
        else stats.disabled++;

        // Add view counts if available
        stats.totalViews += section.views || 0;
        stats.totalInteractions += section.interactions || 0;
      });

      return stats;
    } catch (error) {
      console.error('Error getting organisation section stats:', error);
      return {
        total: 0,
        byType: {},
        enabled: 0,
        disabled: 0,
        totalViews: 0,
        totalInteractions: 0
      };
    }
  }
};